<template>
  <AppWrapper>
    <!-- Used to preserve scroll in the view when fullscreen (fixed position) enabled -->
    <div v-if="stubElementHeight" class="w-full" :style="{ height: `${stubElementHeight}px` }"></div>

    <div
      ref="rootElement"
      class="flex flex-col overflow-hidden outline-none"
      :class="[
        props.class,
        isFullscreen ? 'h-auto! bg-black fixed z-1 inset-0 self-stretch' : 'bg-primary6 relative min-h-60 rounded-2',
      ]"
      :style="style"
      tabindex="-1"
      @click="emit('click', $event)"
    >
      <NLoadingBarProvider container-class="position-absolute! left-1! right-1! z-1" :to="rootElement ?? undefined">
        <AppHeader v-if="inModal" />

        <div class="flex justify-center relative overflow-auto flex-auto" :style="{ aspectRatio }">
          <TableOfContents v-if="isOpenTableOfContents" />

          <CSpin v-if="isLoading" :color="themeVars.primary" size="large" />
          <MainContent v-else />
        </div>

        <AppFooter v-if="!data?.hide_footer" />
      </NLoadingBarProvider>
    </div>
  </AppWrapper>
</template>

<script lang="ts" setup>
import { onMounted, ref, toRef, watch } from 'vue'
import { NLoadingBarProvider } from 'naive-ui'
import MainContent from '@collection-player/components/MainContent.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useTracking from '@lib/apps/collection-player/composables/tracking.use'
import type { CollectionPlayer } from '@collection-player/types/collection-player.type'
import TableOfContents from '@collection-player/components/TableOfContents.vue'
import type { Api, Emit, Options } from '@collection-player/stores/app.store'
import { provideAppStore } from '@collection-player/stores/app.store'
import AppFooter from '@collection-player/components/AppFooter.vue'
import AppHeader from '@collection-player/components/AppHeader.vue'
import AppWrapper from '@collection-player/components/AppWrapper.vue'
import useFocusTrap from '@collection-player/composables/focus-trap.use'
import CSpin from '@lib/components/CSpin/CSpin.vue'

type Style = string | Record<string, any> | Array<string | Record<string, any>>

const props = defineProps<{
  isLoading?: boolean
  data: CollectionPlayer
  id?: string
  isEditMode?: boolean
  inModal?: boolean
  options?: Options
  api: Api
  style?: Style
  class?: Style
  aspectRatio?: number
}>()

const emit = defineEmits<Emit>()
const data = toRef(props, 'data')
const isEditMode = toRef(props, 'isEditMode')
const inModal = toRef(props, 'inModal')
const isLoading = toRef(props, 'isLoading')
const themeVars = useThemeVars()
const rootElement = ref<HTMLElement | null>(null)
const stubElementHeight = ref<number | null>(null)

const {
  activeSlide,
  isSwiperVisible,
  isOpenTableOfContents,
  fullscreen: { isFullscreen },
  visibleGroups,
  activeGroup,
  setActiveSlide,
  keyboardBindings: { addKeyboardListener, removeKeyboardListener },
  slideDirection: { updateSlideDirection },
  api,
  handleKeyAction,
  updateResolvedData,
} = provideAppStore({
  data,
  isEditMode,
  rootElement,
  emit,
  inModal,
  options: props.options,
  api: props.api,
  isLoading: isLoading,
  nodeId: props.id,
})

defineExpose({ isFullscreen, handleKeyAction, element: rootElement, updateResolvedData })
useTracking({ nodeId: props.id, isSwiperVisible, activeSlide, api })

const focusWithin = useFocusTrap(rootElement, { preventFocusLostOnly: true })

onMounted(() => {
  watch(
    [isFullscreen, inModal],
    ([_isFullscreen, _inModal]) => {
      if (_isFullscreen && !_inModal) {
        stubElementHeight.value = rootElement.value?.clientHeight ?? null
      } else {
        stubElementHeight.value = null
      }

      if (_isFullscreen || _inModal) {
        focusWithin.enable()
        addKeyboardListener()
      } else {
        focusWithin.disable()
        removeKeyboardListener()
      }
    },
    { immediate: true },
  )

  watch(
    [visibleGroups, isLoading],
    ([_visibleGroups, _isLoading]) => {
      if (_isLoading) {
        return
      }

      updateSlideDirection(_visibleGroups)
    },
    { immediate: true },
  )

  watch(
    visibleGroups,
    () => {
      if (
        (activeGroup.value && !visibleGroups.value.find((group) => group.id === activeGroup.value?.id)) ||
        (!activeGroup.value && visibleGroups.value.length)
      ) {
        setActiveSlide(data.value?.groups[0])
      }
    },
    { immediate: true },
  )

  watch(
    data,
    () => {
      activeGroup.value =
        (activeGroup.value && data.value?.groups.find((group) => group.id === activeGroup.value?.id)) ??
        data.value?.groups?.[0] ??
        null

      activeSlide.value =
        (activeSlide.value && activeGroup.value?.slides.find((slide) => slide.id === activeSlide.value?.id)) ??
        activeGroup.value?.slides?.[0] ??
        null
    },
    { immediate: true, deep: true },
  )
})
</script>

<style lang="scss" scoped>
.collection-player {
  &__box {
    &--active {
      &:after {
        user-select: none;
        content: '';
        border: 4px solid v-bind('themeVars.primary');
        position: absolute;
        border-radius: 4px;
        left: 0;
        right: 0;
        bottom: 0;
        top: 0;
        z-index: 1;
      }
    }
  }
}
</style>
